import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useState, type MouseEvent } from 'react';
import * as api from '../lib/api';
import { STATUS_COLUMNS } from '../lib/taskStatus';
import TaskHeartbeatControl from './TaskHeartbeatControl';
import { useToast } from './Toast';

/** Shared, compact task controls for list lanes without nested buttons. */
export default function TaskRowActions({ task, onUpdated, onDeleted }: {
  task: api.Task;
  onUpdated: (task: api.Task) => void;
  onDeleted: (taskId: string) => void;
}) {
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const stop = (event: MouseEvent<HTMLElement>) => event.stopPropagation();
  const remove = async (event: MouseEvent<HTMLButtonElement>) => {
    stop(event);
    if (deleting || !window.confirm(`Delete task #${task.seq}?`)) return;
    setDeleting(true);
    try { await api.deleteTask(task.id); onDeleted(task.id); showToast('Task deleted'); }
    catch (error) { showToast(error instanceof Error ? error.message : 'Failed to delete task'); }
    finally { setDeleting(false); }
  };
  return <>
    <span className="personal-task-actions" onClick={stop}>
      <TaskHeartbeatControl task={task} size={14} onUpdated={onUpdated} />
      <button type="button" className="personal-task-action" aria-label={`Edit ${task.title}`} onClick={() => setEditing(true)}><EditOutlined /></button>
      <button type="button" className="personal-task-action" aria-label={`Delete ${task.title}`} disabled={deleting} onClick={remove}><DeleteOutlined /></button>
    </span>
    {editing && <TaskRowEditor task={task} onClose={() => setEditing(false)} onUpdated={(updated) => { onUpdated(updated); setEditing(false); }} />}
  </>;
}

function TaskRowEditor({ task, onClose, onUpdated }: { task: api.Task; onClose: () => void; onUpdated: (task: api.Task) => void }) {
  const { showToast } = useToast();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [status, setStatus] = useState(task.status);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle || saving) return;
    setSaving(true);
    try {
      const updates = { ...(trimmedTitle !== task.title ? { title: trimmedTitle } : {}), ...(description !== task.description ? { description } : {}), ...(status !== task.status ? { status } : {}) };
      if (!Object.keys(updates).length) return onClose();
      onUpdated(await api.updateTask(task.id, updates));
    } catch (error) { showToast(error instanceof Error ? error.message : 'Failed to update task'); setSaving(false); }
  };
  return <div className="personal-task-dialog-backdrop" role="presentation" onMouseDown={onClose}><section className="personal-task-dialog" role="dialog" aria-modal="true" aria-labelledby="task-row-editor-title" onMouseDown={(event) => event.stopPropagation()}>
    <h2 id="task-row-editor-title">Edit task</h2><label>Title<input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} /></label><label>Description<textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} /></label><label>Status<select value={status} onChange={(event) => setStatus(event.target.value as api.Task['status'])}>{STATUS_COLUMNS.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}</select></label><div className="personal-task-dialog-actions"><button type="button" onClick={onClose}>Cancel</button><button type="button" onClick={save} disabled={!title.trim() || saving}>{saving ? 'Saving…' : 'Save'}</button></div>
  </section></div>;
}
